import type { SlotDto } from '@ffs/shared';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { ApiError } from '../lib/api';

export const REFRESH_MS = 60_000;

export interface SlotsState {
  slots: SlotDto[] | null;
  error: string | null;
  loading: boolean;
}

/**
 * Loads slots for a timezone, re-fetches when it changes, and refreshes every 60 s while
 * mounted (FR-SCH-04/07). Stale responses (after a tz change) are ignored.
 */
export function useSlots<T extends { slots: SlotDto[] }>(
  fetcher: (tz: string) => Promise<T>,
  tz: string,
  onData?: (data: T) => void,
): SlotsState & { reload: () => Promise<void> } {
  const [state, set] = useState<SlotsState>({ slots: null, error: null, loading: true });
  const seq = useRef(0);
  const latest = useRef({ fetcher, onData });
  latest.current = { fetcher, onData };

  const load = useCallback(async () => {
    const id = ++seq.current;
    try {
      const data = await latest.current.fetcher(tz);
      if (id !== seq.current) return;
      latest.current.onData?.(data);
      set({ slots: data.slots, error: null, loading: false });
    } catch (e) {
      if (id !== seq.current) return;
      const msg =
        e instanceof ApiError && e.code === 'webinar_inactive'
          ? e.message
          : 'Could not load available times. Please try again.';
      set((s) => ({ slots: s.slots, error: msg, loading: false }));
    }
  }, [tz]);

  useEffect(() => {
    set((s) => ({ ...s, loading: true }));
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      clearInterval(timer);
      seq.current++;
    };
  }, [load]);

  return { ...state, reload: load };
}
